/**
 * A letter's text, as the school wrote it in its template, read into the
 * parts of a formal letter so the PDF can set each one properly:
 *
 *   Subject: Fine for late arrival        → subject line
 *   Dear Parent/Guardian,                 → salutation
 *   Fine Amount: 3,000                    → details table (a run of short
 *   Reason for Fine: Repeated lateness      "Label: value" lines; a label
 *                                           with no value is left out)
 *   Regards,  /  {School} Administration  → sign-off
 *   anything else                          → a paragraph per line
 */

export type LetterBlock =
  | { kind: 'subject'; text: string }
  | { kind: 'salutation'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'details'; rows: Array<[string, string]> }
  | { kind: 'signoff'; closing: string; lines: string[] };

const SUBJECT = /^subject\s*:\s*/i;
const SALUTATION = /^(dear|respected|to)\b.{0,70}$/i;
const CLOSING = /^((kind|best|warm|with)\s+)?(regards|sincerely|thanks|thank you)[,;.]?$|^yours\s+(sincerely|faithfully|truly)[,;.]?$/i;
// A short label (four words at most), a colon, then a space or nothing — "Fine Amount: 3000",
// not prose that happens to contain a colon, nor a time like "8:15".
const DETAIL = /^([A-Za-z][A-Za-z0-9 .’'/()&-]{0,32}?)\s*:(?:\s+(.*))?$/;
const isDetail = (line: string) => {
  const m = DETAIL.exec(line);
  return !!m && m[1].trim().split(/\s+/).length <= 4 && !/^https?$/i.test(m[1].trim());
};

export function letterBlocks(content: string): LetterBlock[] {
  const lines = content.replace(/\r/g, '').split('\n').map(l => l.trim());
  const out: LetterBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line) { i += 1; continue; }

    if (CLOSING.test(line)) {
      // Everything after the closing word signs the letter.
      const rest = lines.slice(i + 1).filter(Boolean);
      out.push({ kind: 'signoff', closing: `${line.replace(/[,;.]$/, '')},`, lines: rest });
      break;
    }
    if (SUBJECT.test(line)) {
      out.push({ kind: 'subject', text: line.replace(SUBJECT, '') });
    } else if (isDetail(line)) {
      const rows: Array<[string, string]> = [];
      while (i < lines.length && lines[i] && isDetail(lines[i]) && !SUBJECT.test(lines[i])) {
        const [, label, value] = DETAIL.exec(lines[i])!;
        if (value?.trim()) rows.push([label.trim(), value.trim()]);
        i += 1;
      }
      if (rows.length) out.push({ kind: 'details', rows });
      continue;
    } else if (!out.some(b => b.kind === 'paragraph' || b.kind === 'salutation') && SALUTATION.test(line)) {
      out.push({ kind: 'salutation', text: line });
    } else {
      out.push({ kind: 'paragraph', text: line });
    }
    i += 1;
  }
  return out;
}
